/* eslint-disable @typescript-eslint/strict-boolean-expressions */
import PaymentsIcon from '@mui/icons-material/Payments'
import {
  Box,
  Grid,
  Link,
  Radio,
  Tooltip,
  Typography,
  styled
} from '@mui/material'
import { InvoiceCloudFrame, MButton, ProgressLoaders, Text } from 'components'
import QuickLinks from 'components/layouts/AuthorizedLayout/component/QuickLinks'
import { MESSAGES, USER_TOKEN } from 'config'
import { color } from 'config/color'
import { useAuth } from 'context'
import dayjs from 'dayjs'
import { useSnackbar } from 'notistack'
import { useEffect, useMemo, useState } from 'react'
import { APIs } from 'services'
import { formatValue, useLocalStorage } from 'utils'
import { consolidateServiceAddresses, type ConsolidatedAddress } from 'utils/helpers/consolidateServiceAddresses'
import { useMobileBreakpoints } from 'utils/hooks/useMobileBreakpoints'

export interface IUserValue {
  name?: string
  officeNumber?: string
  mobileNumber?: string
  mailingAddress?: string
  email?: string
  balance?: string
  alteredEmail?: string
  serviceAddress?: string[]
}

export const Account: React.FC = () => {
  const { account, propManAccount, billing, isAdmin, userEmail, currentAccount, getAccount, handleCurrentAccount, getBilling } = useAuth()
  const { enqueueSnackbar } = useSnackbar()
  const [authToken] = useLocalStorage(USER_TOKEN, '')
  const [active, setActive] = useState<number>(0)
  const [userValue, setUserValue] = useState<IUserValue>()
  const [defaultAccountId, setDefaultAccountId] = useState<any>(null)
  const isMobile = useMobileBreakpoints()
  const billURL = billing?.billing[billing.billing.length - 1]?.billDisplayURL ?? null
  const [prevAccountId, setPrevAccountId] = useState<string>('')
  const [consolidatedAddress, setConsolidatedAddress] = useState<ConsolidatedAddress[]>()

  const consolidatedAddresses = useMemo(() => {
    const serviceAddress = propManAccount?.myAccount?.serviceAddresses ?? account?.myAccount?.serviceAddresses
    if (serviceAddress) {
      return consolidateServiceAddresses(serviceAddress)
    }
    return []
  }, [propManAccount, account])

  useEffect(() => {
    setConsolidatedAddress(consolidatedAddresses)
  }, [consolidatedAddresses])

  useEffect(() => {
    if (prevAccountId === currentAccount?.accountId || currentAccount?.accountId === account?.myAccount?.accountId) {
      return
    }
    if (currentAccount?.accountId !== null) {
      void getAccount({
        AccessToken: authToken,
        accountId: currentAccount?.accountId,
        admin: isAdmin,
        email: userEmail
      })
    }
    setPrevAccountId((currentAccount?.accountId ?? account?.myAccount?.accountId) ?? '')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authToken, currentAccount])

  useEffect(() => {
    if (account) {
      const serviceAddresses = account?.myAccount?.serviceAddresses.map(item => item.serviceAddress)
      setUserValue({
        name: account?.myAccount?.personName,
        officeNumber: account?.myAccount?.alternateNumber,
        mobileNumber: account?.myAccount?.mobileNumber,
        mailingAddress: `${account?.myAccount?.mailingAddress?.addressLine1}, ${account?.myAccount?.mailingAddress?.city}, ${account?.myAccount?.mailingAddress?.state} ${account?.myAccount?.mailingAddress?.postal}`,
        email: account?.myAccount?.email,
        balance:
          account?.myAccount?.accountBalance &&
          formatValue(account?.myAccount.accountBalance),
        alteredEmail: 'ND',
        serviceAddress: serviceAddresses
      })
    }
    if (!billing) {
      void getBilling({
        AccessToken: authToken,
        accountId: account?.myAccount.accountId,
        startDate: dayjs().subtract(1, 'year').format('YYYY-MM-DD'),
        endDate: dayjs().format('YYYY-MM-DD'),
        admin: isAdmin,
        email: userEmail
      })
    }
  }, [account, billing])

  useEffect(() => {
    if (account && !defaultAccountId) {
      setDefaultAccountId(account?.myAccount.defaultAccountId)
    }
  }, [account, defaultAccountId])


  const handleCheckBox = async (item: any): Promise<void> => {
    try {
      const body: any = {
        AccessToken: authToken,
        accountId: account?.myAccount.accountId,
        admin: isAdmin,
        email: userEmail
      }
      body.updates = [
        {
          object: 'DEFAULTACCOUNT',
          value: item.accountId
        }
      ]
      await APIs.updateUser(body)
      enqueueSnackbar(MESSAGES.ACCOUNT_UPDATED_SUCCESS, {
        variant: 'success',
        autoHideDuration: 3000
      })
      setDefaultAccountId(item.accountId)
      handleCurrentAccount({ accountId: item.accountId ?? '', premiseId: item?.premiseId ?? '' })
      void getAccount({
        AccessToken: authToken,
        accountId: item.accountId
      })
    } catch (e) {
      enqueueSnackbar(MESSAGES.ACCOUNT_UPDATED_ERROR, {
        variant: 'error',
        autoHideDuration: 3000
      })
    }
  }

  return (
    <>
      {isMobile && <QuickLinks />}
      {account !== null
        ? <Grid container spacing={2} mt={0}>
          <Grid item sm={12} md={8}>
            <Wrapper gap={2}>
              <BoxWrapper>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
                  <Typography color={color.grey[900]} fontSize={isMobile ? 18 : 24} fontWeight="600">
                    General Information
                  </Typography>
                </Box>

                {(userValue != null)
                  ? <GeneralInformation userValue={userValue} />
                  : <ProgressLoaders height="100%" />}


              </BoxWrapper>

              <BoxWrapper>
                <Typography color={color.grey[900]} fontSize={isMobile ? 18 : 24} fontWeight="600">
                  Service Address
                </Typography>
                <Box my={2}>
                  <Text color={color.grey[800]} fontSize="16px" inline="true">
                    Please select a service address from the list below to be
                    displayed by default when you log in to your account portal.
                    If you do not see your service address listed below, please
                    contact &nbsp;
                  </Text>
                  <Text color={color.background[900]} fontSize="16px" inline="true">
                    877-818-2637
                  </Text>
                  <Text color={color.grey[800]} fontSize="16px" inline="true">
                    .
                  </Text>
                </Box>

                {consolidatedAddress?.filter(item => !item?.isAdminLinked).map(
                  // filter out admin linked accounts
                  (item: ConsolidatedAddress, index: number) => (
                    <Box
                      key={index}
                      border={`solid 1px ${color.white}`}
                      onClick={() => { setActive(0) }}
                    >
                      <Box
                        p={1}
                        display="flex"
                        alignItems="center"
                        bgcolor={active === 0 ? color.background[50] : color.white}
                      >
                        <Radio
                          onChange={async () => { await handleCheckBox(item) }}
                          // disabled={!!propManAccount || item?.isAdminLinked}
                          disabled
                          checked={item?.accountId === defaultAccountId && true}
                        />
                        <Box>
                          <Typography fontSize={10} color={color.grey[700]}>
                            ACCOUNT NUMBER
                          </Typography>
                          <Typography fontSize={16} color={color.grey[900]}>
                            {item?.accountId}
                          </Typography>
                        </Box>
                      </Box>
                      {isMobile
                        ? <Box p={1}>
                          <Box display="flex" flexDirection="column" mb={2}>
                            <Typography flex={1} color={color.grey[700]} fontSize={10}>
                              PROPERTY ADDRESS
                            </Typography>
                          </Box>
                          {item.serviceAddresses.map((serviceAddress, index) => {
                            return <Typography key={index} flex={1} color={color.grey[800]} fontSize={14}>
                              {serviceAddress?.serviceAddress}
                            </Typography>
                          })}
                          <Box mt={1} display="flex" flexDirection="column" >
                            <Typography flex={1} color={color.grey[700]} fontSize={10}>
                              OLD ACCOUNT NUMBER
                            </Typography>
                            <Typography flex={1} color={color.grey[800]} fontSize={14}>
                              {item?.legacyAcctNumber}
                            </Typography>
                          </Box>
                        </Box>
                        : <Box m={1}>
                          <Box display="flex" p={1} borderBottom={`1px solid ${color.background[50]}`}>
                            <Typography flex={1} color={color.grey[700]} fontSize={10}>
                              PROPERTY ADDRESS
                            </Typography>
                            <Typography flex={1} color={color.grey[700]} fontSize={10}>
                              OLD ACCOUNT NUMBER
                            </Typography>
                          </Box>
                          {item.serviceAddresses.map((serviceAddress, index) => {
                            return <Box key={index} display="flex" p={1}>
                              <Typography flex={1} color={color.grey[800]} fontSize={16}>
                                {serviceAddress.serviceAddress}
                              </Typography>
                              {index === 0 && <Typography flex={1} color={color.grey[800]} fontSize={16}>
                                {item?.legacyAcctNumber}
                              </Typography>}
                            </Box>
                          })}
                        </Box>}
                    </Box>
                  )
                )}
              </BoxWrapper>
            </Wrapper>
          </Grid>
          <Grid item sm={12} md={4}>
            <BoxWrapper>
              <Box display="flex" alignItems="center" mb={3}>
                <PaymentsIcon fontSize='medium' sx={{ color: color.secondary[500] }} />
                <Typography ml={1} color={color.grey[900]} fontSize={isMobile ? 18 : 24} fontWeight="600">
                  Current Bill Detail
                </Typography>
              </Box>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                mb={3}
              >
                <Text color={color.black} fontSize="18px">
                  Total Due
                </Text>
                <Box
                  p="12px 16px"
                  sx={{ background: color.white, borderRadius: '4px' }}
                >
                  <Text color={color.grey[900]} fontSize="24px" fontWeight="600">
                    {userValue?.balance}
                  </Text>
                </Box>
              </Box>
              <InvoiceCloudFrame page={1}>
                <MButton
                  fullWidth
                  rounded="true"
                  variant="contained"
                  texttransform='none'
                  size="large"
                >
                  Pay Bill
                </MButton>
              </InvoiceCloudFrame>
              <Typography color={color.grey[700]} fontSize={16} my={2} textAlign={isMobile ? 'center' : 'left'}>
                To download your invoice,&nbsp;
                <Tooltip
                  placement="top"
                  title={!billURL ? 'Invoice not available, please try again later.' : ''}
                >
                  <Link target="_blank" rel="noopener noreferrer" href={billURL ?? undefined}>
                    click here.
                  </Link>
                </Tooltip>
              </Typography>
              <Typography color={color.grey[700]} fontSize={14}>
                Did you know you can now pay your bill using Apple Pay, Google
                Pay, Venmo, or Paypal?
              </Typography>

            </BoxWrapper>
          </Grid >
        </Grid >
        : <Box mt={3}><ProgressLoaders height="100%" /></Box>}
    </>

  )
}

const GeneralInformation = ({ userValue }: { userValue: IUserValue }): JSX.Element => {
  return (
    <Grid borderRadius="4px" container spacing={2}>
      <Grid item xs={12} md={6}>
        <Box mb={2.5}>
          <Text color={color.grey[800]} fontSize="12px" margin="0 0 2px">
            NAME
          </Text>
          <Text color={color.grey[900]} fontSize="16px">
            {userValue?.name}
          </Text>
        </Box>
      </Grid>
      <Grid item xs={12} md={6}>
        <Box mb={2.5}>
          <Text color={color.grey[800]} fontSize="12px" margin="0 0 2px">
            HOME / OFFICE NUMBER
          </Text>
          <Text color={color.grey[900]} fontSize="16px">
            {userValue && (userValue.mobileNumber ?? userValue.officeNumber)}
          </Text>
        </Box>
      </Grid>
      <Grid item xs={12} md={6}>
        <Box mb={2.5}>
          <Text color={color.grey[800]} fontSize="12px" margin="0 0 2px">
            MAILING ADDRESS
          </Text>
          <Text color={color.grey[900]} fontSize="16px">
            {userValue?.mailingAddress}
          </Text>
        </Box>
      </Grid>
      <Grid item xs={12} md={6}>
        <Box mb={2.5}>
          <Text color={color.grey[800]} fontSize="12px" margin="0 0 2px">
            EMAIL ID
          </Text>
          <Text color={color.grey[900]} fontSize="16px">
            {userValue?.email}
          </Text>
        </Box>
      </Grid>
    </Grid>
  )
}

const Wrapper = styled(Box)`
  display: flex;
  flex-direction: column;
`

export const BoxWrapper = styled(Box)({
  background: color.white,
  borderRadius: '4px',
  padding: '24px'
})
